import { FRESHNESS_TAGS, type FreshnessTag } from '@brushfire/types'

const MONTHS: Record<Exclude<FreshnessTag, 'PERM'>, number> = {
  R1M: 1,
  R3M: 3,
  R6M: 6,
  R12M: 12,
}

const STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'is',
  'your',
  'you',
  'what',
  'do',
  'does',
  'are',
  'of',
  'to',
  'and',
  'or',
  'please',
  'how',
  'which',
  'this',
  'that',
  'for',
])

/** Jaccard threshold for “Similar Profile Update Question Found”. */
export const PROFILE_SIMILARITY_THRESHOLD = 0.45

/**
 * Refresh eligible at = observed at + freshness interval (calendar months, UTC).
 * `PERM` has no scheduled refresh date.
 */
export function addFreshnessInterval(observedAt: Date, tag: FreshnessTag): Date | null {
  if (tag === 'PERM') return null
  const months = MONTHS[tag]
  const result = new Date(observedAt.getTime())
  const day = result.getUTCDate()
  result.setUTCMonth(result.getUTCMonth() + months)
  if (result.getUTCDate() < day) {
    result.setUTCDate(0)
  }
  return result
}

export function isKnownFreshnessTag(value: string): value is FreshnessTag {
  return (FRESHNESS_TAGS as readonly string[]).includes(value)
}

export function tokenizeProfileText(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((token) => token.length > 1 && !STOP_WORDS.has(token))
}

/** Token overlap of the prompt and attribute name. 1 when the normalized names are equal. */
export function profileQuestionSimilarity(
  input: { prompt: string; attributeName?: string },
  candidate: { prompt: string; attributeName: string },
): number {
  const leftName = (input.attributeName ?? '').trim().toLowerCase()
  const rightName = candidate.attributeName.trim().toLowerCase()
  if (leftName.length > 0 && leftName === rightName) return 1

  const left = new Set([
    ...tokenizeProfileText(input.prompt),
    ...tokenizeProfileText(input.attributeName ?? ''),
  ])
  const right = new Set([
    ...tokenizeProfileText(candidate.prompt),
    ...tokenizeProfileText(candidate.attributeName),
  ])
  if (left.size === 0 || right.size === 0) return 0
  let intersection = 0
  for (const token of left) {
    if (right.has(token)) intersection += 1
  }
  const union = left.size + right.size - intersection
  return union === 0 ? 0 : intersection / union
}

/** “What is your current employment status?” → “Employment Status”. */
export function suggestProfileAttributeName(prompt: string): string {
  let text = prompt.trim().replace(/[?\s.]+$/g, '')
  text = text.replace(
    /^(what is your|what's your|what are your|please (tell us|select) your|tell us your)\s+/i,
    '',
  )
  text = text.replace(/^(current|currently)\s+/i, '')
  text = text.trim()
  if (!text) return 'Profile attribute'
  const titled = text
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  return titled.slice(0, 80)
}
